/**
 * Bug hunt - floating report button + in-place report sheet (FEAT-008).
 *
 * Replaces the old BugReportPage: reporting happens in a bottom sheet over the
 * current screen, so the reporter stays exactly where the bug is and the
 * captured route is the screen being reported, not /report-bug.
 */
import { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Bug, ImagePlus, X } from 'lucide-react';
import { api } from '../api/client';
import Sheet from '../molecules/Sheet';
import Button from '../atoms/Button';
import { useBugHuntStore } from '../store/bugHuntStore';
import { useUpdateStore } from '../store/updateStore';
import { useToastStore } from '../store/toastStore';
import { getTheme } from '../utils/theme';

const MAX_SHOTS = 6;

export default function BugHunt() {
  const enabled = useBugHuntStore((s) => s.enabled);
  const sheetOpen = useBugHuntStore((s) => s.sheetOpen);
  const sentCount = useBugHuntStore((s) => s.sentCount);
  const appVersion = useUpdateStore((s) => s.appVersion);
  const location = useLocation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [note, setNote] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    setFiles((prev) => [...prev, ...Array.from(list)].slice(0, MAX_SHOTS));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!note.trim()) {
      setError('Describe what happened first');
      return;
    }
    setSending(true);

    const form = new FormData();
    form.append('note', note.trim());
    form.append(
      'context',
      JSON.stringify({
        version: appVersion,
        route: location.pathname + location.search,
        theme: getTheme(),
        userAgent: navigator.userAgent,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        online: navigator.onLine,
        standalone: window.matchMedia('(display-mode: standalone)').matches,
      })
    );
    files.forEach((f) => form.append('screenshots', f));

    const response = await api.postFormData('/bug-reports', form, { timeout: 30000 });
    setSending(false);
    if (response.success) {
      setNote('');
      setFiles([]);
      useToastStore.getState().show('Report sent. It went straight to the fix list.');
      useBugHuntStore.getState().reportSent();
    } else {
      setError(response.error?.message || 'Could not send. Check your signal and try again.');
    }
  };

  return (
    <>
      {enabled && !sheetOpen && (
        <button
          type="button"
          onClick={() => useBugHuntStore.getState().openSheet()}
          aria-label="Report a problem"
          className="fixed right-2 top-[30vh] z-[60] flex h-11 w-11 items-center justify-center rounded-full border border-border bg-surface/80 shadow-lg backdrop-blur-xl transition-transform active:scale-95"
        >
          <Bug size={20} className="text-text-secondary" />
          {sentCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-text-inverse">
              {sentCount}
            </span>
          )}
        </button>
      )}

      <Sheet
        open={sheetOpen}
        onClose={() => useBugHuntStore.getState().closeSheet()}
        title="Report a Problem"
      >
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-4 pb-4 pt-3">
          {error && <div className="rounded-lg bg-error-light p-3 text-sm text-error">{error}</div>}

          <label htmlFor="bug-note" className="text-sm font-medium text-text">
            What happened?
          </label>
          <textarea
            id="bug-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={5000}
            rows={5}
            placeholder="What did you do, what did you expect, what happened instead?"
            className="w-full rounded-xl border border-border bg-background p-3 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary"
            disabled={sending}
          />

          <div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending || files.length >= MAX_SHOTS}
              className="flex items-center gap-2 rounded-lg bg-background px-3 py-2 text-sm text-text-secondary disabled:opacity-50"
            >
              <ImagePlus size={18} />
              Add screenshots ({files.length}/{MAX_SHOTS})
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = '';
              }}
            />
            {files.length > 0 && (
              <ul className="mt-2 space-y-1">
                {files.map((f, i) => (
                  <li
                    key={i}
                    className="flex items-center justify-between rounded bg-background px-2 py-1 text-xs text-text-secondary"
                  >
                    <span className="truncate">{f.name}</span>
                    <button
                      type="button"
                      onClick={() => setFiles(files.filter((_, j) => j !== i))}
                      aria-label={`Remove ${f.name}`}
                      className="text-text-muted"
                    >
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <p className="text-xs text-text-muted">
            Your username, app version (v{appVersion}), current screen, and device info are
            attached automatically.
          </p>

          <Button type="submit" variant="primary" fullWidth isLoading={sending}>
            Send Report
          </Button>
        </form>
      </Sheet>
    </>
  );
}
